import React from "react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Last Updated:</strong> 4 April 2026<br />
          <strong>Trading Name:</strong> VSENDS<br />
          <strong>Legal Entity:</strong> V CAN RESOURCES LIMITED<br />
          <strong>Company Number:</strong> 13759348<br />
          <strong>Registered Office:</strong> 32 Abbey Drive, Dartford, England, DA2 7WP<br />
          <strong>Support Email:</strong> support@vsends.com<br />
          <strong>Compliance Email:</strong> vg@vcanresources.com
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Introduction</h2>
        <p className="text-gray-700 mb-4">
          V CAN RESOURCES LIMITED ("we", "us", "our") is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the VSENDS platform and related services (the "Service").
        </p>
        <p className="text-gray-700 mb-4">
          We are the data controller for the personal data we process in connection with the Service. If you have any questions about this Privacy Policy or our data practices, please contact us at vg@vcanresources.com.
        </p>
        <p className="text-gray-700 mb-4">
          This Privacy Policy applies to all users of the Service, including account holders, recipients of emails sent through the Service, and visitors to our website. By using the Service, you consent to the practices described in this policy.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Information We Collect</h2>
        <p className="text-gray-700 mb-4">
          We collect several types of information from and about users of our Service, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Account Information:</strong> Name, email address, company name, phone number, and billing address when you register for an Account.</li>
          <li><strong>Payment Information:</strong> Credit card details and billing information processed securely through our payment providers. We do not store full credit card numbers on our servers.</li>
          <li><strong>Usage Data:</strong> Information about how you interact with the Service, including login times, features used, emails sent, open rates, click rates, and other analytics.</li>
          <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers, and other technical information collected automatically.</li>
          <li><strong>Communication Data:</strong> The content of emails you send through the Service, including recipient lists, subject lines, and message bodies.</li>
          <li><strong>Support Data:</strong> Information you provide when contacting our support team, including the content of your communications.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. How We Collect Information</h2>
        <p className="text-gray-700 mb-4">
          We collect information through the following methods:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Directly from you:</strong> When you register an Account, update your profile, make a purchase, or contact our support team.</li>
          <li><strong>Automatically:</strong> Through cookies, web beacons, log files, and other tracking technologies as you use the Service.</li>
          <li><strong>From third parties:</strong> From our payment processors, analytics providers, and business partners.</li>
          <li><strong>From your contacts:</strong> When you upload contact lists or recipient data to the Service, we process that information on your behalf as a data processor.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Legal Basis for Processing</h2>
        <p className="text-gray-700 mb-4">
          Under the UK GDPR and the Data Protection Act 2018, we rely on the following lawful bases to process your personal data:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Contractual necessity:</strong> Processing is necessary to perform the contract governing our provision of the Service.</li>
          <li><strong>Legitimate interests:</strong> Processing is necessary for our legitimate interests, such as improving the Service, preventing fraud, and ensuring security, provided these interests are not overridden by your data protection rights.</li>
          <li><strong>Consent:</strong> Where you have given your explicit consent for specific processing activities, such as receiving marketing communications from us.</li>
          <li><strong>Legal obligation:</strong> Processing is necessary for compliance with a legal obligation to which we are subject.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. How We Use Your Information</h2>
        <p className="text-gray-700 mb-4">
          We use the information we collect for the following purposes:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>To provide, maintain, and improve the Service.</li>
          <li>To process transactions and send related information, including confirmations, invoices, and technical notices.</li>
          <li>To respond to your comments, questions, and support requests.</li>
          <li>To send you technical notices, updates, security alerts, and support and administrative messages.</li>
          <li>To monitor and analyse trends, usage, and activities in connection with the Service.</li>
          <li>To detect, investigate, and prevent fraudulent transactions and other illegal activities.</li>
          <li>To personalise and improve your experience with the Service.</li>
          <li>To comply with legal obligations and enforce our Terms of Service.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Data Sharing and Disclosure</h2>
        <p className="text-gray-700 mb-4">
          We may share your personal data with the following categories of recipients:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Service Providers:</strong> Third-party companies and individuals that perform services on our behalf, including payment processing, data analysis, email delivery, hosting, and customer service.</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your personal data may be transferred as part of that transaction.</li>
          <li><strong>Legal Requirements:</strong> Where required by law, regulation, legal process, or governmental request.</li>
          <li><strong>Protection of Rights:</strong> To protect the rights, property, or safety of V CAN RESOURCES LIMITED, our users, or the public.</li>
          <li><strong>With Your Consent:</strong> We may share your data with third parties when you give us your consent to do so.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We do not sell your personal data to third parties. All third-party processors are bound by data processing agreements that require them to protect your data in accordance with applicable law.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Data Retention</h2>
        <p className="text-gray-700 mb-4">
          We retain your personal data for as long as your Account is active or as needed to provide you with the Service. We will also retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
        </p>
        <p className="text-gray-700 mb-4">
          Specific retention periods include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Account data:</strong> Retained for the duration of your Account plus 30 days after deletion, unless a longer period is required by law.</li>
          <li><strong>Billing records:</strong> Retained for 7 years as required by UK tax law.</li>
          <li><strong>Usage logs:</strong> Retained for 12 months for security and analytics purposes.</li>
          <li><strong>Support communications:</strong> Retained for 3 years after the last interaction.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Data Security</h2>
        <p className="text-gray-700 mb-4">
          We implement appropriate technical and organisational measures to protect your personal data against unauthorised or unlawful processing, accidental loss, destruction, or damage. These measures include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Encryption of data in transit using TLS 1.2 or higher.</li>
          <li>Encryption of data at rest using AES-256 encryption.</li>
          <li>Regular security assessments and penetration testing.</li>
          <li>Access controls and authentication mechanisms.</li>
          <li>Employee training on data protection and security best practices.</li>
          <li>Incident response procedures for data breaches.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          While we strive to protect your personal data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. International Data Transfers</h2>
        <p className="text-gray-700 mb-4">
          Your personal data may be transferred to and processed in countries other than the country in which you reside. These countries may have data protection laws that differ from those in the United Kingdom.
        </p>
        <p className="text-gray-700 mb-4">
          Where we transfer your data outside the UK, we ensure appropriate safeguards are in place, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Adequacy decisions by the UK government.</li>
          <li>UK International Data Transfer Agreements or Addendums.</li>
          <li>Standard contractual clauses approved by the Information Commissioner's Office (ICO).</li>
        </ul>
        <p className="text-gray-700 mb-4">
          You may contact us at vg@vcanresources.com for more information about the specific safeguards applied to your data when it is transferred internationally.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Your Rights</h2>
        <p className="text-gray-700 mb-4">
          Under the UK GDPR and the Data Protection Act 2018, you have the following rights:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Right of access:</strong> You may request a copy of the personal data we hold about you.</li>
          <li><strong>Right to rectification:</strong> You may request correction of inaccurate or incomplete data.</li>
          <li><strong>Right to erasure:</strong> You may request deletion of your personal data in certain circumstances.</li>
          <li><strong>Right to restrict processing:</strong> You may request that we limit how we use your data.</li>
          <li><strong>Right to data portability:</strong> You may request a machine-readable copy of your data.</li>
          <li><strong>Right to object:</strong> You may object to processing based on legitimate interests or direct marketing.</li>
          <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, you may withdraw it at any time.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          To exercise any of these rights, please contact us at vg@vcanresources.com. We will respond to your request within 30 days. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at www.ico.org.uk.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. Data Processing as a Processor</h2>
        <p className="text-gray-700 mb-4">
          When you use the Service to send communications to your contacts, we act as a data processor on your behalf. You are the data controller for the personal data of your contacts that you upload to or process through the Service.
        </p>
        <p className="text-gray-700 mb-4">
          As a processor, we:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Process personal data only on your documented instructions.</li>
          <li>Ensure that persons authorised to process the data are committed to confidentiality.</li>
          <li>Implement appropriate technical and organisational security measures.</li>
          <li>Assist you in responding to data subject requests.</li>
          <li>Notify you without undue delay upon becoming aware of a personal data breach.</li>
          <li>Delete or return all personal data upon termination of the Service.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Our Data Processing Agreement (DPA) sets out the full details of our obligations as a processor. The DPA is incorporated into our Terms of Service.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">12. Cookies and Tracking Technologies</h2>
        <p className="text-gray-700 mb-4">
          We use cookies and similar tracking technologies to collect and track information about your activity on the Service. These technologies help us understand user behaviour, improve the Service, and measure the effectiveness of our marketing efforts.
        </p>
        <p className="text-gray-700 mb-4">
          You can control cookies through your browser settings and other tools. For more information about the specific cookies we use and how to manage them, please refer to our Cookie Policy.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">13. Children's Privacy</h2>
        <p className="text-gray-700 mb-4">
          The Service is not intended for children under the age of 16. We do not knowingly collect personal data from children under 16. If we become aware that we have inadvertently collected personal data from a child under 16, we will take steps to delete such information promptly.
        </p>
        <p className="text-gray-700 mb-4">
          If you believe that we have collected personal data from a child under 16, please contact us immediately at vg@vcanresources.com.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">14. Changes to This Privacy Policy</h2>
        <p className="text-gray-700 mb-4">
          We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by posting the updated policy on our website with a new "Last Updated" date.
        </p>
        <p className="text-gray-700 mb-4">
          We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information. Your continued use of the Service after changes take effect constitutes your acceptance of the updated policy.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">15. Contact Us</h2>
        <p className="text-gray-700 mb-4">
          If you have any questions, concerns, or requests regarding this Privacy Policy or our data protection practices, please contact us:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>By email:</strong> vg@vcanresources.com</li>
          <li><strong>By post:</strong> V CAN RESOURCES LIMITED, 32 Abbey Drive, Dartford, England, DA2 7WP</li>
          <li><strong>Support:</strong> support@vsends.com</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Our Data Protection Officer can be reached at vg@vcanresources.com. We will respond to all inquiries within 30 days.
        </p>
      </main>
    </div>
  );
}
